function toPoint(lng, lat) {
    lng = Number(lng);
    lat = Number(lat);
    if (!isFinite(lng) || !isFinite(lat)) return null;
    if (lng === 0 && lat === 0) return null;
    return { lng, lat };
}

export function parseLngLat(location) {
    if (location == null || location === '') return null;
    if (Array.isArray(location)) {
        if (location.length === 0) return null;
        if (location.length >= 2 && typeof location[0] !== 'object') {
            return toPoint(location[0], location[1]);
        }
        return null;
    }
    if (typeof location === 'object') {
        let lng = location.lng != null ? location.lng : location.longitude;
        let lat = location.lat != null ? location.lat : location.latitude;
        return toPoint(lng, lat);
    }
    let loc = String(location).replace(/\s/g, '').split(',');
    if (loc.length < 2) return null;
    return toPoint(loc[0], loc[1]);
}

export function isValidPoint(lng, lat) {
    return !!toPoint(lng, lat);
}

function loadPlugin(name) {
    return new Promise((resolve) => {
        if (!window.AMap) {
            resolve(false);
            return;
        }
        let shortName = String(name).replace('AMap.', '');
        if (window.AMap[shortName]) {
            resolve(true);
            return;
        }
        window.AMap.plugin(name, function () {
            resolve(true);
        });
    });
}

const COARSE_GEO_LEVELS = ['国家', '省', '市', '区县', '开发区', '乡镇', '村庄', '热点商圈'];

function isPreciseGeocode(geocode) {
    let level = geocode && geocode.level;
    if (!level) return true;
    return COARSE_GEO_LEVELS.indexOf(level) < 0;
}

function pickMatchedPoi(pois, address) {
    let list = (pois || []).filter((poi) => parseLngLat(poi && poi.location));
    if (!list.length) return null;
    let matched = list.find((poi) => poi.name && address.indexOf(poi.name) >= 0);
    if (matched) return matched;
    matched = list.find((poi) => {
        let name = (poi.name || '').replace(/^[\u4e00-\u9fa5]+?(市|县|区)/, '');
        return name.length >= 4 && address.indexOf(name) >= 0;
    });
    return matched || list[0];
}

function formatPoiAddress(poi, fallback) {
    let addr = poi && poi.address && typeof poi.address === 'string' ? poi.address : '';
    if (poi && poi.name) {
        return addr ? `${poi.name}(${addr})` : poi.name;
    }
    return addr || fallback;
}

function geocodeAddress(address, city) {
    return new Promise((resolve) => {
        address = (address || '').trim();
        if (!address || !window.AMap) {
            resolve(null);
            return;
        }
        loadPlugin('AMap.Geocoder').then(() => {
            let geocoder = new window.AMap.Geocoder({
                city: city || '全国'
            });
            geocoder.getLocation(address, function (status, result) {
                if (status === 'complete' && result && result.geocodes && result.geocodes[0]) {
                    let geocode = result.geocodes[0];
                    if (!isPreciseGeocode(geocode)) {
                        resolve(null);
                        return;
                    }
                    let point = parseLngLat(geocode.location);
                    if (!point) {
                        resolve(null);
                        return;
                    }
                    resolve({
                        lng: point.lng,
                        lat: point.lat,
                        address: geocode.formattedAddress || address,
                        source: 'geocode'
                    });
                    return;
                }
                resolve(null);
            });
        });
    });
}

function placeTextSearch(address, city) {
    return new Promise((resolve) => {
        address = (address || '').trim();
        if (!address || !window.AMap) {
            resolve(null);
            return;
        }
        loadPlugin('AMap.PlaceSearch').then(() => {
            let placeSearch = new window.AMap.PlaceSearch({
                pageSize: 10,
                pageIndex: 1,
                city: city || '全国',
                extensions: 'base'
            });
            placeSearch.search(address, function (status, result) {
                if (status !== 'complete' || !result || !result.poiList || !result.poiList.pois) {
                    resolve(null);
                    return;
                }
                let poi = pickMatchedPoi(result.poiList.pois || [], address);
                if (!poi) {
                    resolve(null);
                    return;
                }
                let point = parseLngLat(poi.location);
                resolve({
                    lng: point.lng,
                    lat: point.lat,
                    address: formatPoiAddress(poi, address),
                    source: 'place-text'
                });
            });
        });
    });
}

function mapPoiToTip(poi) {
    let point = parseLngLat(poi && poi.location);
    if (!point) return null;
    return {
        name: poi.name || '',
        address: poi.address && typeof poi.address === 'string' ? poi.address : '',
        district: `${poi.pname || ''}${poi.cityname || ''}${poi.adname || ''}`,
        location: `${point.lng},${point.lat}`
    };
}

function searchByPlace(keywords) {
    return new Promise((resolve) => {
        loadPlugin('AMap.PlaceSearch').then(() => {
            let placeSearch = new window.AMap.PlaceSearch({
                pageSize: 10,
                pageIndex: 1,
                city: '全国'
            });
            placeSearch.search(keywords, function (status, result) {
                if (status !== 'complete' || !result || !result.poiList) {
                    resolve([]);
                    return;
                }
                let tips = (result.poiList.pois || []).map(mapPoiToTip).filter(Boolean);
                resolve(tips);
            });
        });
    });
}

export function searchInputTips(keywords, location) {
    return new Promise((resolve) => {
        keywords = (keywords || '').trim();
        if (!keywords || !window.AMap) {
            resolve([]);
            return;
        }
        loadPlugin('AMap.AutoComplete').then((ok) => {
            let AutoComplete = window.AMap.AutoComplete || window.AMap.Autocomplete;
            if (!ok || !AutoComplete) {
                searchByPlace(keywords).then(resolve);
                return;
            }
            let options = {
                city: '全国'
            };
            if (location) options.location = location;
            let auto = new AutoComplete(options);
            auto.search(keywords, function (status, result) {
                let tips = [];
                if (status === 'complete' && result && result.tips) {
                    tips = result.tips.filter((item) => {
                        return item && item.name && parseLngLat(item.location);
                    });
                }
                if (tips.length) {
                    resolve(tips);
                    return;
                }
                searchByPlace(keywords).then(resolve);
            });
        });
    });
}

export async function resolveAddressLocation(address, city) {
    address = (address || '').trim();
    if (!address) {
        return { ok: false, reason: '地址为空' };
    }
    let search = await placeTextSearch(address, city);
    if (search) return { ok: true, ...search };
    let tips = await searchInputTips(address);
    if (tips && tips.length) {
        let tip = tips.find((item) => item.name && address.indexOf(item.name) >= 0) || tips[0];
        let point = parseLngLat(tip && tip.location);
        if (point) {
            return {
                ok: true,
                lng: point.lng,
                lat: point.lat,
                address: tip.name || address,
                source: 'tips'
            };
        }
    }
    let geo = await geocodeAddress(address, city);
    if (geo) return { ok: true, ...geo };
    return { ok: false, reason: '高德未能解析该地址' };
}

export function regeoLocation(point) {
    return new Promise((resolve) => {
        let longitude = point.longitude != null ? point.longitude : point.lng;
        let latitude = point.latitude != null ? point.latitude : point.lat;
        if (!window.AMap || !isValidPoint(longitude, latitude)) {
            resolve(null);
            return;
        }
        loadPlugin('AMap.Geocoder').then(() => {
            let geocoder = new window.AMap.Geocoder();
            geocoder.getAddress([Number(longitude), Number(latitude)], function (status, result) {
                if (status === 'complete' && result && result.regeocode) {
                    resolve({
                        lng: Number(longitude),
                        lat: Number(latitude),
                        address: result.regeocode.formattedAddress
                    });
                    return;
                }
                resolve(null);
            });
        });
    });
}
